import { faker } from '@faker-js/faker';

import type { EndpointReturnType } from '@cast/api-client';

/**
 * Upstream query-error fixtures (POLY-2202 pre-API mocks).
 *
 * Rows, issue types and totals all derive from the same catalog so the
 * numbers stay consistent. Faker is re-seeded on every call, so refetches
 * return identical data; timeseries values jitter around the average.
 */

type QueryErrorsResponse = EndpointReturnType<'dbo', 'getQueryErrors'>;
type QueryErrorDetailResponse = EndpointReturnType<
  'dbo',
  'getQueryErrorDetail'
>;
type QueryErrorQueriesResponse = EndpointReturnType<
  'dbo',
  'getQueryErrorQueries'
>;
type QueryIssueTypeId = QueryErrorsResponse['issueTypes'][number]['id'];

const DEFAULT_RANGE_MS = 60 * 60 * 1000;
const MAX_POINTS = 500;
const FIXTURE_SEED = 42601;

type ErrorSeed = {
  id: string;
  name: string;
  issueType: QueryIssueTypeId;
  description: string;
  details: string;
  /** Raw upstream error string */
  message: string;
  occurrences: number;
  queriesCount: number;
};

const ERROR_CATALOG: ErrorSeed[] = [
  {
    id: '42601',
    name: 'Syntax error',
    issueType: 'syntax_error',
    description: "The database couldn't read the query",
    details:
      "The database couldn't understand the query as written. This usually means a typo or a missing value in the query your application builds.",
    message: 'syntax error at or near "?"',
    occurrences: 527,
    queriesCount: 89,
  },
  {
    id: '42P01',
    name: 'Table missing',
    issueType: 'table_missing',
    description: "The table or view the statement targets doesn't exist",
    details:
      "The statement references a table or view that doesn't exist in the database. It was likely dropped, renamed, or the query targets the wrong schema.",
    message: 'relation "orders_archive" does not exist',
    occurrences: 350,
    queriesCount: 12,
  },
  {
    id: '57014',
    name: 'Query timeout',
    issueType: 'query_timeout',
    description: 'The query ran longer than the database allows',
    details:
      'The database cancelled the query because it exceeded the configured statement timeout. It may need an index, a rewrite, or a higher timeout.',
    message: 'canceling statement due to statement timeout',
    occurrences: 268,
    queriesCount: 34,
  },
  {
    id: '22P02',
    name: 'Invalid value',
    issueType: 'invalid_value',
    description: 'A value provided in the wrong format for its column',
    details:
      'A bound parameter could not be cast to the column type — for example a non-numeric string bound to a numeric column.',
    message: 'invalid input syntax for type numeric: "12a4"',
    occurrences: 247,
    queriesCount: 41,
  },
  {
    id: '25P02',
    name: 'Aborted',
    issueType: 'aborted',
    description: 'An earlier failure cancelled the rest of the transaction',
    details:
      'The transaction was already aborted by a previous error, but the application kept sending statements inside it. Fix the first error in the transaction.',
    message:
      'current transaction is aborted, commands ignored until end of transaction block',
    occurrences: 206,
    queriesCount: 57,
  },
  {
    id: '22008',
    name: 'Date out of range',
    issueType: 'other',
    description: "A date or time value wasn't valid",
    details:
      'A date/time parameter fell outside the valid range, such as a non-existent calendar date.',
    message: 'date/time field value out of range: "2026-02-30"',
    occurrences: 165,
    queriesCount: 23,
  },
];

const ISSUE_TYPES: Array<{ id: QueryIssueTypeId; label: string }> = [
  { id: 'syntax_error', label: 'Syntax error' },
  { id: 'query_timeout', label: 'Query timeout' },
  { id: 'table_missing', label: 'Table missing' },
  { id: 'invalid_value', label: 'Invalid value' },
  { id: 'aborted', label: 'Aborted' },
  { id: 'other', label: 'Other' },
];

const CATALOG_TOTAL = ERROR_CATALOG.reduce(
  (sum, error) => sum + error.occurrences,
  0
);

/** Total query volume behind the errors (≈1.9% error share, per Figma). */
const TOTAL_QUERIES = 108368;

const totalFor = (issueType: QueryIssueTypeId): number =>
  ERROR_CATALOG.filter((error) => error.issueType === issueType).reduce(
    (sum, error) => sum + error.occurrences,
    0
  );

const toMs = (value?: string): number | undefined => {
  if (!value) return undefined;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : undefined;
};

const resolveTimestamps = (startTime?: string, endTime?: string): number[] => {
  const end = toMs(endTime) ?? Date.now();
  const start = Math.min(
    toMs(startTime) ?? end - DEFAULT_RANGE_MS,
    end - 60000
  );
  const count = Math.min(
    Math.max(Math.floor((end - start) / 60000) + 1, 2),
    MAX_POINTS
  );
  const stepMs = (end - start) / (count - 1);
  return Array.from({ length: count }, (_, index) => start + index * stepMs);
};

/** Chart-ready point header: ISO timestamp + unix seconds. */
const toPoint = (ms: number) => ({
  timestamp: new Date(ms).toISOString(),
  unixTimestamp: Math.floor(ms / 1000),
});

/** `count` values jittered around `total / count`. */
const spread = (total: number, count: number): number[] => {
  const average = Math.floor(total / count);
  const jitter = Math.max(1, Math.round(average * 0.15));
  return Array.from({ length: count }, () =>
    Math.max(0, average + faker.datatype.number({ min: -jitter, max: jitter }))
  );
};

/** Random recent timestamp, 5 minutes to 12 hours back. */
const minutesAgo = (): string =>
  new Date(
    Date.now() - faker.datatype.number({ min: 5, max: 720 }) * 60000
  ).toISOString();

export const getQueryErrorsFixture = (params: {
  instanceId?: string;
  startTime?: string;
  endTime?: string;
  database?: string;
}): QueryErrorsResponse => {
  faker.seed(FIXTURE_SEED);

  const timestamps = resolveTimestamps(params.startTime, params.endTime);

  const issueTypes = ISSUE_TYPES.map(({ id, label }) => {
    const occurrences = totalFor(id);
    return { id, label, occurrences, share: occurrences / CATALOG_TOTAL };
  });

  const points = {
    syntax_error: spread(totalFor('syntax_error'), timestamps.length),
    query_timeout: spread(totalFor('query_timeout'), timestamps.length),
    table_missing: spread(totalFor('table_missing'), timestamps.length),
    invalid_value: spread(totalFor('invalid_value'), timestamps.length),
    aborted: spread(totalFor('aborted'), timestamps.length),
    other: spread(totalFor('other'), timestamps.length),
  };

  return {
    totals: {
      totalErrors: CATALOG_TOTAL,
      errorShareOfQueries: CATALOG_TOTAL / TOTAL_QUERIES,
      issueTypesCount: ISSUE_TYPES.length,
    },
    issueTypes,
    timeseries: timestamps.map((ms, index) => ({
      ...toPoint(ms),
      syntax_error: points.syntax_error[index],
      query_timeout: points.query_timeout[index],
      table_missing: points.table_missing[index],
      invalid_value: points.invalid_value[index],
      aborted: points.aborted[index],
      other: points.other[index],
    })),
    items: ERROR_CATALOG.map((error) => ({
      id: error.id,
      name: error.name,
      description: error.description,
      occurrences: error.occurrences,
      errorShare: error.occurrences / CATALOG_TOTAL,
      queriesCount: error.queriesCount,
      lastSeen: minutesAgo(),
    })).sort((a, b) => b.occurrences - a.occurrences),
  };
};

export const getQueryErrorDetailFixture = (params: {
  instanceId?: string;
  errorId: string;
}): QueryErrorDetailResponse | undefined => {
  faker.seed(FIXTURE_SEED);

  const error = ERROR_CATALOG.find(({ id }) => id === params.errorId);
  if (!error) return undefined;

  const timestamps = resolveTimestamps().slice(-60);
  const points = spread(error.occurrences, timestamps.length);

  return {
    id: error.id,
    name: error.name,
    description: error.description,
    details: error.details,
    message: error.message,
    occurrences: error.occurrences,
    errorShare: error.occurrences / CATALOG_TOTAL,
    queriesCount: error.queriesCount,
    lastSeen: minutesAgo(),
    timeseries: timestamps.map((ms, index) => ({
      ...toPoint(ms),
      occurrences: points[index],
    })),
  };
};

type StatementSeed = {
  templateHash: string;
  name: string;
  statement: string;
};

const STATEMENT_POOL: StatementSeed[] = [
  {
    templateHash: '1042299784242042d315',
    name: 'SELECT id, total FROM orders WHERE c…',
    statement:
      "SELECT id, total FROM orders WHERE customer_id = $1 AND created_at > now() - interval '30 days' ORDER BY created_at DESC LIMIT 50",
  },
  {
    templateHash: '7f3c9a51be2042d315',
    name: 'SELECT id, total FROM orders WHERE c…',
    statement:
      "SELECT id, total FROM orders WHERE customer_id = $1 AND status = 'paid'",
  },
  {
    templateHash: '2b81cc09f7e5114c8d2a',
    name: 'INSERT INTO order_items (order_id, s…',
    statement:
      'INSERT INTO order_items (order_id, sku, quantity, unit_price) VALUES ($1, $2, $3, $4)',
  },
  {
    templateHash: '9d4e77b2c3a08f1605ee',
    name: 'UPDATE products SET cluster_id = $1,…',
    statement:
      'UPDATE products SET cluster_id = $1, updated_at = now() WHERE sku = $2',
  },
  {
    templateHash: '5c02ffa831b7d64e9c10',
    name: 'SELECT id AS workbench_id, w.cluste…',
    statement:
      'SELECT id AS workbench_id, w.cluster_id, w.owner_id FROM workbenches w WHERE w.owner_id = $1',
  },
  {
    templateHash: 'a1f8d02c67e45b39aa77',
    name: 'SELECT runtime_rules.id, runtime_ru…',
    statement:
      'SELECT runtime_rules.id, runtime_rules.type, runtime_rules.deleted_at FROM runtime_rules WHERE runtime_rules.organization_id = $1 AND runtime_rules.is_built_in = false',
  },
  {
    templateHash: 'e6b19c47d0f2a58331bd',
    name: 'DELETE FROM sessions WHERE user_id …',
    statement: 'DELETE FROM sessions WHERE user_id = $1 AND expires_at < now()',
  },
  {
    templateHash: '3fa52d8b9c0e1746ab25',
    name: 'UPDATE users SET last_login_at = n…',
    statement:
      'UPDATE users SET last_login_at = now(), login_count = login_count + 1 WHERE email = $1',
  },
];

export const getQueryErrorQueriesFixture = (params: {
  instanceId?: string;
  errorId: string;
  startTime?: string;
  endTime?: string;
}): QueryErrorQueriesResponse | undefined => {
  faker.seed(FIXTURE_SEED);

  const error = ERROR_CATALOG.find(({ id }) => id === params.errorId);
  if (!error) return undefined;

  const statements = faker.helpers
    .shuffle(STATEMENT_POOL)
    .slice(0, faker.datatype.number({ min: 4, max: 6 }));

  const weights = statements.map(() =>
    faker.datatype.number({ min: 1, max: 10 })
  );
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);

  return {
    items: statements.map((statement, index) => {
      const occurrences = Math.max(
        1,
        Math.round((weights[index] / weightSum) * error.occurrences)
      );
      return {
        templateHash: statement.templateHash,
        name: statement.name,
        statement: statement.statement,
        occurrences,
        errorShare: occurrences / error.occurrences,
        lastSeen: minutesAgo(),
      };
    }),
  };
};
